import { BadgePercent, Sparkles } from '@music-vine/cadence/icons';
import { Button, Text } from '@music-vine/cadence/ui';
import Link from 'next/link';
import { PageDialog } from '@/components/PageDialog/PageDialog';
import { usePlanPrices } from '@/components/DialogStage/scenes/download/sections/usePlanPrices';
import { cn } from '@/lib/utils';
import { ReferredVisitorPopupBenefits } from './Benefits';
import { getCustomText } from './copy';
import { getAdvertisedDiscount, shouldShowBenefits } from './variant';
import type { CheckoutPlanSlug } from '@/components/DialogStage/scenes/download/sections/usePlanPrices';
import type { ReactNode } from 'react';
import type { ReferredVisitorPopupContent } from './types';
const STORAGE_KEY = 'referred-visitor-popup-c';
const PRICING_HREF = '/pricing';
const PERCENT = 100;
const DISCOUNTED_PLANS: {
  slug: CheckoutPlanSlug;
  label: string;
}[] = [{
  slug: 'creator',
  label: 'Creator'
}, {
  slug: 'pro',
  label: 'Pro'
}];
const dismissDialog = () => {
  window.postMessage({
    type: 'DISMISS_DIALOGUE',
    storageKey: STORAGE_KEY
  }, window.location.origin);
};
const openMobileDrawer = () => {
  window.postMessage({
    type: 'OPEN_DIALOGUE_DRAWER'
  }, window.location.origin);
};
const applyDiscount = (price: number, percentage: number) => Math.round(price * (PERCENT - percentage)) / PERCENT;
type Copy = {
  eyebrow: string;
  title: string;
  description: string;
  cta: string;
  chip: ReactNode;
};
const getCopy = (content: ReferredVisitorPopupContent): Copy => {
  const {
    creatorName
  } = content;
  const discountPercentage = getAdvertisedDiscount(content);
  const custom = getCustomText(content);
  if (discountPercentage === null) {
    return {
      eyebrow: 'YOUR CREATIVE TOOLKIT',
      title: 'Everything your content needs, one place',
      description: `We're happy you found us through ${creatorName}. Over 150,000 assets, from royalty-free music to sound effects, stock video, motion graphics, and LUTs, all human-made and fully rights-cleared.`,
      cta: 'Start exploring',
      chip: <>
          <span className="font-semibold">Welcome</span> to Uppbeat!
        </>,
      ...custom
    };
  }
  return {
    eyebrow: 'DISCOUNT UNLOCKED',
    title: `Join now with ${discountPercentage}% off`,
    description: `You're getting ${discountPercentage}% off Creator and Pro for joining through ${creatorName}'s link. The discount is automatically applied at checkout, no code needed.`,
    cta: `Claim my ${discountPercentage}% off`,
    chip: <>
        <span className="font-semibold">{discountPercentage}% OFF</span>{' '}
        unlocked!
      </>,
    ...custom
  };
};
const PriceComparison = ({
  discountPercentage
}: {
  discountPercentage: number;
}) => {
  const {
    symbol,
    priceFor
  } = usePlanPrices();
  return <dl className="flex flex-col divide-y divide-gray-200 rounded-lg bg-gray-100 px-5 dark:divide-gray-700 dark:bg-gray-800" data-sentry-component="PriceComparison" data-sentry-source-file="VariantC.tsx">
      {DISCOUNTED_PLANS.map(({
      slug,
      label
    }) => {
      const price = Number(priceFor(slug));
      const discountedPrice = applyDiscount(price, discountPercentage);
      return <div className="flex items-center justify-between gap-4 py-3" key={slug}>
            <dt className="font-semibold text-gray-950 text-sm dark:text-white">
              {label}
            </dt>
            <dd className="flex items-center gap-2 text-sm">
              <s className="text-gray-500 dark:text-gray-400">
                <span className="sr-only">Was </span>
                {symbol}
                {price.toFixed(2)}
              </s>
              <span className="text-brand-primary">
                <span className="sr-only">now </span>
                {symbol}
                {discountedPrice.toFixed(2)}
              </span>
              <span className="sr-only">per month, billed yearly</span>
            </dd>
          </div>;
    })}
    </dl>;
};
const PopupBody = ({
  copy,
  discountPercentage,
  exploreHref,
  isDrawer,
  showBenefits,
  showEyebrow,
  onCtaClick
}: {
  copy: Copy;
  discountPercentage: number | null;
  exploreHref: string;
  isDrawer: boolean;
  showBenefits: boolean;
  showEyebrow: boolean;
  onCtaClick?: (href: string) => void;
}) => {
  const href = discountPercentage === null ? exploreHref : PRICING_HREF;
  return <div className={cn('flex flex-col gap-6 dark:bg-gray-875', !isDrawer && 'px-10 pt-8 pb-10')} data-sentry-component="PopupBody" data-sentry-source-file="VariantC.tsx">
      <div className="flex flex-col gap-2 text-center">
        {!!showEyebrow && <p className="text-brand-primary text-sm uppercase">{copy.eyebrow}</p>}
        <h2 className={cn('text-balance font-semibold', isDrawer ? 'text-xl' : 'text-2xl')}>
          {copy.title}
        </h2>
        <Text className="text-pretty text-gray-700 text-sm dark:text-gray-50" data-sentry-element="Text" data-sentry-source-file="VariantC.tsx">
          {copy.description}
        </Text>
      </div>

      {discountPercentage !== null && <PriceComparison discountPercentage={discountPercentage} />}

      {showBenefits && <ReferredVisitorPopupBenefits />}

      <Button asChild className="w-full" fontSize="sm" size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="VariantC.tsx">
        <Link href={href} onClick={() => {
        onCtaClick?.(href);
        dismissDialog();
      }} data-sentry-element="Link" data-sentry-source-file="VariantC.tsx">
          {copy.cta}
        </Link>
      </Button>
    </div>;
};
export interface ReferredVisitorPopupVariantCProps {
  content: ReferredVisitorPopupContent;
  /** Milliseconds before the pop-up appears. */
  delay?: number;
  /** Where the no-discount "Start exploring" CTA goes. */
  exploreHref?: string;
  /** Fires once the pop-up has closed, whichever way it was dismissed (X, overlay, drawer, CTA). */
  onClose: () => void;
  /** Fires on CTA click with the destination, before the pop-up closes. */
  onCtaClick?: (href: string) => void;
  /** Fires when the pop-up becomes visible. */
  onShow?: () => void;
}
export const ReferredVisitorPopupVariantC = ({
  content,
  onClose,
  onCtaClick,
  onShow,
  delay = 0,
  exploreHref = '/music'
}: ReferredVisitorPopupVariantCProps) => {
  const {
    creatorName,
    imageUrl
  } = content;
  const discountPercentage = getAdvertisedDiscount(content);
  const copy = getCopy(content);
  const showBenefits = shouldShowBenefits(content);
  const ChipIcon = discountPercentage === null ? Sparkles : BadgePercent;
  return <PageDialog delay={delay} dismissPermanently={false} drawerChildren={<PopupBody copy={copy} discountPercentage={discountPercentage} exploreHref={exploreHref} isDrawer onCtaClick={onCtaClick} showBenefits={showBenefits} showEyebrow />} image={imageUrl ? {
    src: imageUrl,
    alt: creatorName,
    badge: copy.eyebrow
  } : undefined} mobileChildren={<div className="flex items-center justify-between gap-3 px-3 py-3">
          <div className="flex items-center gap-2">
            <div className="flex rounded-full bg-brand-secondary p-2.5 dark:bg-pink-950">
              <ChipIcon className="size-4 text-pink-600" />
            </div>
            <p className="text-base text-gray-950 dark:text-white">
              {copy.chip}
            </p>
          </div>
          <button className="flex shrink-0 items-center font-semibold text-base text-pink-600" onClick={openMobileDrawer} type="button">
            Learn more
          </button>
        </div>} onClickDismiss={onClose} onShow={onShow} storageKey={STORAGE_KEY} title={copy.title} data-sentry-element="PageDialog" data-sentry-component="ReferredVisitorPopupVariantC" data-sentry-source-file="VariantC.tsx">
      <PopupBody copy={copy} discountPercentage={discountPercentage} exploreHref={exploreHref} isDrawer={false} onCtaClick={onCtaClick} showBenefits={showBenefits} showEyebrow={!imageUrl} data-sentry-element="PopupBody" data-sentry-source-file="VariantC.tsx" />
    </PageDialog>;
};